/**
 * The name a clone gets: required, and checked against the server as it is typed.
 * A clone never takes a name an instance or dataset already has (specs/clone-packages.md).
 */
import React, { useEffect, useState } from 'react';
import { Input, Label, FieldHelp } from '../InstanceSettings/formPrimitives';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { nameAvailable } from './cloneApi';

const DEBOUNCE_MS = 300;

export default function CloneNameField({ id, kind, value, onChange, onAvailable, placeholder }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ status: 'idle' });

  useEffect(() => {
    onAvailable(false);
    if (!value.trim()) { setState({ status: 'idle' }); return undefined; }
    setState({ status: 'checking' });
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const r = await nameAvailable(kind, value);
        if (cancelled) return;
        setState(r.available ? { status: 'free' } : { status: 'taken', conflict: r.conflict, reason: r.reason });
        onAvailable(!!r.available);
      } catch (err) {
        if (!cancelled) setState({ status: 'error', error: err.message });
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // onAvailable is the parent's setter; re-running on its identity would loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, value]);

  return (
    <div>
      <Label htmlFor={id}>{t('clone.nameLabel')}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required />
      {state.status === 'checking' && <FieldHelp>{t('clone.checking')}</FieldHelp>}
      {state.status === 'free' && <FieldHelp>{t('clone.nameFree')}</FieldHelp>}
      {state.status === 'taken' && (
        <FieldHelp style={{ color: '#f87171' }}>
          {state.conflict ? t('clone.nameTaken', { name: state.conflict.name }) : t('clone.nameInvalid')}
        </FieldHelp>
      )}
      {state.status === 'error' && <FieldHelp style={{ color: '#f87171' }}>{t('clone.failed', { error: state.error })}</FieldHelp>}
    </div>
  );
}
